import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Settings, Smartphone } from 'lucide-react';
import { useSelectedMetrics, isMetricSelected, getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
import { colonnesVideoGoogleAds, cumulerVideo, TOTAUX_VIDEO_INITIAUX } from '../utils/metriquesVideo';

/**
 * Au niveau module, et non dans le composant : `metricConfig` la reçoit en argument, donc la
 * lit dès l'exécution du `useMemo`. Déclarée plus bas dans le corps du composant, elle était
 * encore dans sa zone morte — « Cannot access 'renderVariation' before initialization ».
 * Elle ne dépend d'aucun état, ce placement est donc sans effet de bord.
 */
const renderVariation = makeRenderVariation({ fontSize: '0.8em', style: { marginTop: 0 } });
/**
 * Composant table modulaire des appareils avec graphique et tableau
 * Optimisé avec React.memo et useMemo pour les performances
 * Supporte le filtrage par labels via requête API
 */
const DevicesTable = React.memo(({
  charts,
  campaignsData = [],
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  selectedAccount = null,
  selectedLabelsFilter = [],
  campaignMatchesLabelsFilter = () => true,
  COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8'],
  // Nouvelles props pour le filtrage par labels via API
  labelFilteredCampaignIds = null,
  token = null,
  selectedPeriod = 'LAST_30_DAYS',
  customStartDate = null,
  customEndDate = null,
  // Props pour la comparaison de périodes
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = 'previous_period',
  loadingComparison = false,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'devices'
}) => {
  // États locaux pour cette table
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Configuration des métriques pour ce composant
  const componentMetrics = useComponentMetrics('devices', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Configuration du rendu des métriques (utilisé pour l'ordre dynamique)
  const metricConfig = useMemo(() => ({
    impressions: {
      label: 'Impressions',
      sortKey: 'impressions',
      renderCell: (device, prevDevice, hasComparison) => (
        <>
          <div>{formatNumber(device.impressions || 0)}</div>
          {hasComparison && renderVariation(calculateVariation(device.impressions || 0, prevDevice.impressions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.impressions)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
        </>
      )
    },
    ctr: {
      label: 'CTR',
      sortKey: 'ctr',
      renderCell: (device, prevDevice, hasComparison) => (
        <>
          {formatNumber(device.ctr || 0, 2)}%
          {hasComparison && renderVariation(calculateVariation(device.ctr || 0, prevDevice?.ctr || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.ctr, 2)}%</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
        </>
      )
    },
    clics: {
      label: 'Clics',
      sortKey: 'clicks',
      renderCell: (device, prevDevice, hasComparison) => (
        <>
          <div>{formatNumber(device.clicks || 0)}</div>
          {hasComparison && renderVariation(calculateVariation(device.clicks || 0, prevDevice.clicks || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.clicks)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
        </>
      )
    },
    cpc: {
      label: 'CPC',
      sortKey: 'cpc',
      renderCell: (device, prevDevice, hasComparison) => (
        <>
          <div>{formatNumber(device.cpc || 0, 2)}€</div>
          {hasComparison && renderVariation(
            calculateVariation(device.cpc || 0, prevDevice?.clicks > 0 ? (prevDevice.cost || 0) / prevDevice.clicks : 0),
            true
          )}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.cpc, 2)}€</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
        </>
      )
    },
    cout: {
      label: 'Coût',
      sortKey: 'cost',
      renderCell: (device, prevDevice, hasComparison) => (
        <>
          <div>{formatNumber(device.cost || 0, 2)}€</div>
          {hasComparison && renderVariation(calculateVariation(device.cost || 0, prevDevice.cost || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.cost, 2)}€</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
        </>
      )
    },
    taux_conv: {
      label: 'Taux conv.',
      sortKey: 'conversionRate',
      renderCell: (device, prevDevice, hasComparison) => (
        <>
          {formatNumber(device.conversionRate || 0, 2)}%
          {hasComparison && renderVariation(calculateVariation(device.conversionRate || 0, prevDevice?.conversionRate || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.conversionRate, 2)}%</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
        </>
      )
    },
    conv: {
      label: 'Conv.',
      sortKey: 'value',
      renderCell: (device, prevDevice, hasComparison) => (
        <>
          <div>{formatNumber(device.conversions || 0, 0)}</div>
          {hasComparison && renderVariation(calculateVariation(device.conversions || 0, prevDevice.conversions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.conversions, 0)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
        </>
      )
    },
    cpa: {
      label: 'CPA',
      sortKey: 'cpa',
      getClassName: (device) => getCpaClassName(device.cpa || 0, selectedAccount?.id || 'global', moduleId),
      getTotalClassName: (totals) => getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId),
      renderCell: (device, prevDevice, hasComparison) => (
        <>
          <div>{formatNumber(device.cpa || 0, 2)}€</div>
          {hasComparison && renderVariation(
            calculateVariation(device.cpa || 0, prevDevice?.conversions > 0 ? (prevDevice.cost || 0) / prevDevice.conversions : 0),
            true
          )}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.cpa, 2)}€</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
        </>
      )
    },
    valeur_conv: {
      label: 'Valeur Conv.',
      sortKey: 'conversionsValue',
      renderCell: (device, prevDevice, hasComparison) => (
        <>
          <div>{formatNumber(device.conversionsValue || 0, 2)}€</div>
          {hasComparison && renderVariation(calculateVariation(device.conversionsValue || 0, prevDevice.conversionsValue || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.conversionsValue, 2)}€</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
        </>
      )
    },
    cos: {
      label: 'COS',
      sortKey: 'cos',
      getClassName: (device) => getCosClassName(device.cos || 0, selectedAccount?.id || 'global', moduleId),
      getTotalClassName: (totals) => getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId),
      renderCell: (device, prevDevice, hasComparison) => (
        <>
          <div>{formatNumber(device.cos || 0, 1)}%</div>
          {hasComparison && renderVariation(
            calculateVariation(device.cos || 0, prevDevice?.conversionsValue > 0 ? ((prevDevice.cost || 0) / prevDevice.conversionsValue) * 100 : 0),
            true
          )}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.cos, 1)}%</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
        </>
      )
    },
    panier_moyen: {
      label: 'Panier moyen',
      sortKey: 'panierMoyen',
      renderCell: (device, prevDevice, hasComparison) => {
        const panierMoyen = (device.conversions || 0) > 0 ? (device.conversionsValue || 0) / device.conversions : 0;
        const prevPanierMoyen = prevDevice && (prevDevice.conversions || 0) > 0 ? (prevDevice.conversionsValue || 0) / prevDevice.conversions : 0;
        return (
          <>
            <div>{formatNumber(panierMoyen, 2)}€</div>
            {hasComparison && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const panierMoyen = (totals.conversions || 0) > 0 ? (totals.conversionsValue || 0) / totals.conversions : 0;
        const prevPanierMoyen = previousTotals && (previousTotals.conversions || 0) > 0 ? (previousTotals.conversionsValue || 0) / previousTotals.conversions : 0;
        return (
          <>
            <div><strong>{formatNumber(panierMoyen, 2)}€</strong></div>
            {previousTotals && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      }
    },
    roas: {
      label: 'ROAS',
      sortKey: 'roas',
      renderCell: (device, prevDevice, hasComparison) => {
        const roas = (device.cost || 0) > 0 ? ((device.conversionsValue || 0) / device.cost) * 100 : 0;
        const prevRoas = prevDevice && (prevDevice.cost || 0) > 0 ? ((prevDevice.conversionsValue || 0) / prevDevice.cost) * 100 : 0;
        return (
          <>
            <div>{roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}</div>
            {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roas = (totals.cost || 0) > 0 ? ((totals.conversionsValue || 0) / totals.cost) * 100 : 0;
        const prevRoas = previousTotals && (previousTotals.cost || 0) > 0 ? ((previousTotals.conversionsValue || 0) / previousTotals.cost) * 100 : 0;
        return (
          <>
            <div><strong>{roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}</strong></div>
            {previousTotals && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      }
    },
    roi: {
      label: 'ROI',
      sortKey: 'roi',
      renderCell: (device, prevDevice, hasComparison) => {
        const roi = (device.cost || 0) > 0 ? ((device.conversionsValue || 0) - device.cost) / device.cost : 0;
        const prevRoi = prevDevice && (prevDevice.cost || 0) > 0 ? ((prevDevice.conversionsValue || 0) - prevDevice.cost) / prevDevice.cost : 0;
        return (
          <>
            <div>{formatNumber(roi, 2)}</div>
            {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roi = (totals.cost || 0) > 0 ? ((totals.conversionsValue || 0) - totals.cost) / totals.cost : 0;
        const prevRoi = previousTotals && (previousTotals.cost || 0) > 0 ? ((previousTotals.conversionsValue || 0) - previousTotals.cost) / previousTotals.cost : 0;
        return (
          <>
            <div><strong>{formatNumber(roi, 2)}</strong></div>
            {previousTotals && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      }
    },
    ...colonnesVideoGoogleAds({ formatNumber, renderVariation, calculateVariation })
  }), [formatNumber, selectedAccount?.id]);

  // État pour les données filtrées par labels (chargées via API)
  const [labelFilteredData, setLabelFilteredData] = useState(null);
  const [loadingLabelFilter, setLoadingLabelFilter] = useState(false);

  // États pour les données de période précédente (chargées via API)
  const [previousDevicesData, setPreviousDevicesData] = useState([]);
  const [loadingPrevious, setLoadingPrevious] = useState(false);

  // Hook pour les métriques sélectionnées
  const selectedMetrics = useSelectedMetrics();

  // Fonction pour formater une date en YYYY-MM-DD sans conversion UTC
  const formatLocalDate = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);




  // Charger les données de la période précédente UNIQUEMENT si compareEnabled est true
  useEffect(() => {
    // Si comparaison non activée, vider les données de période précédente
    if (!compareEnabled) {
      setPreviousDevicesData([]);
      return;
    }

    if (!isVisible || !selectedAccount?.id || !token) {
      setPreviousDevicesData([]);
      return;
    }

    // Attendre que previousPeriodData.period soit disponible (calculé par le backend)
    // Cela garantit que toutes les composantes utilisent les mêmes dates de période précédente
    if (!previousPeriodData?.period?.startDate || !previousPeriodData?.period?.endDate) {
      console.log(`📱 [DEVICES] En attente de previousPeriodData.period...`);
      return;
    }

    const loadPreviousData = async () => {
      setLoadingPrevious(true);

      // Utiliser les dates de période précédente calculées par le backend
      // pour être cohérent avec les autres composants (Réseaux, Types de campagnes, Résultats)
      const prevPeriod = {
        startDate: previousPeriodData.period.startDate,
        endDate: previousPeriodData.period.endDate
      };
      console.log(`📱 [DEVICES] Chargement période précédente (depuis backend): ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

      try {
        const response = await axios.post(`${config.API_URL}/api/dashboard/components/devices`, {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: prevPeriod.startDate,
          customEndDate: prevPeriod.endDate,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data?.success && response.data.charts) {
          const deviceChart = response.data.charts.find(chart => chart.id === 'real-device-conversions');
          if (deviceChart) {
            let prevDevices = deviceChart.dataAggregated || [];
            if (!prevDevices.length && deviceChart.data) {
              // Agréger les données par appareil
              const deviceBreakdown = {};
              deviceChart.data.forEach(entry => {
                const name = entry.device || entry.name || 'UNKNOWN';
                if (!deviceBreakdown[name]) {
                  deviceBreakdown[name] = { name, device: name, impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX };
                }
                deviceBreakdown[name].impressions += entry.impressions || 0;
                deviceBreakdown[name].clicks += entry.clicks || 0;
                deviceBreakdown[name].cost += entry.cost || 0;
                deviceBreakdown[name].conversions += entry.conversions || 0;
                deviceBreakdown[name].conversionsValue += entry.conversionsValue || 0;
                cumulerVideo(deviceBreakdown[name], entry);
              });
              prevDevices = Object.values(deviceBreakdown);
            }
            console.log(`📱 [DEVICES] ${prevDevices.length} appareils période précédente`);
            setPreviousDevicesData(prevDevices);
          }
        }
      } catch (error) {
        console.warn(`⚠️ [DEVICES] Erreur chargement période précédente:`, error.message);
        setPreviousDevicesData([]);
      } finally {
        setLoadingPrevious(false);
      }
    };

    loadPreviousData();
  }, [isVisible, compareEnabled, selectedAccount?.id, token, previousPeriodData?.period?.startDate, previousPeriodData?.period?.endDate]);

  // Mapper les données de comparaison par appareil (utilise les données chargées via API)
  const previousDevicesMap = useMemo(() => {
    if (!compareEnabled || !previousDevicesData.length) return {};
    const map = {};
    previousDevicesData.forEach(device => {
      // Normaliser le nom de l'appareil pour la correspondance
      const deviceName = device.device || device.name || 'UNKNOWN';
      map[deviceName] = device;
    });
    return map;
  }, [compareEnabled, previousDevicesData]);

  // Charger les données filtrées par labels via API
  useEffect(() => {
    if (!isVisible) return;

    // Si pas de filtre labels actif ou pas de campaignIds, réinitialiser
    if (!labelFilteredCampaignIds || labelFilteredCampaignIds.length === 0) {
      setLabelFilteredData(null);
      return;
    }

    // Vérifier les conditions nécessaires pour faire l'appel API
    if (!selectedAccount || !token) {
      console.log('📱 [DevicesTable] Pas de compte ou token, skip API call');
      return;
    }

    const loadFilteredDevicesData = async () => {
      try {
        console.log('📱 [DevicesTable] Chargement données filtrées par labels via API');
        console.log('📱 [DevicesTable] campaignIds:', labelFilteredCampaignIds.length);
        setLoadingLabelFilter(true);

        const requestBody = {
          sources: [selectedAccount.id],
          period: selectedPeriod,
          campaignIds: labelFilteredCampaignIds
        };

        // Ajouter les dates personnalisées si période CUSTOM
        if (selectedPeriod === 'CUSTOM' && customStartDate && customEndDate) {
          requestBody.customStartDate = customStartDate;
          requestBody.customEndDate = customEndDate;
        }

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/components/devices`,
          requestBody,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success && response.data.charts?.length > 0) {
          const deviceChartData = response.data.charts.find(c => c.id === 'real-device-conversions');
          if (deviceChartData) {
            console.log('📱 [DevicesTable] Données filtrées par labels reçues:', {
              dataLength: deviceChartData.data?.length,
              dataAggregatedLength: deviceChartData.dataAggregated?.length
            });
            setLabelFilteredData(deviceChartData);
          }
        }
      } catch (error) {
        console.error('📱 [DevicesTable] Erreur chargement données filtrées:', error);
      } finally {
        setLoadingLabelFilter(false);
      }
    };

    loadFilteredDevicesData();
  }, [isVisible, labelFilteredCampaignIds, selectedAccount, token, selectedPeriod, customStartDate, customEndDate]);

  // Extraction des données d'appareils (memoized)
  const deviceChart = useMemo(() => {
    if (!charts || !Array.isArray(charts)) {
      return null;
    }
    return charts.find(chart => chart.id === 'real-device-conversions');
  }, [charts]);


  const deviceData = useMemo(() => {
    // Si on a des données filtrées par labels via API, les utiliser en priorité
    if (labelFilteredData && selectedLabelsFilter.length > 0) {
      console.log('📱 [DevicesTable] Utilisation données filtrées par labels (API)');
      const dataAggregated = labelFilteredData.dataAggregated || labelFilteredData.data || [];
      // Normaliser et calculer les métriques
      // NOTE: Ne pas arrondir les conversions ici pour éviter les erreurs d'agrégation
      return dataAggregated.map(device => ({
        ...device,
        name: device.name || device.device || device.deviceType || 'Appareil inconnu',
        ctr: device.ctr || (device.impressions > 0 ? (device.clicks / device.impressions * 100) : 0),
        cpc: device.cpc || (device.clicks > 0 ? (device.cost / device.clicks) : 0),
        conversions: device.conversions || device.value || 0,
        conversionRate: device.conversionRate || (device.clicks > 0 ? ((device.conversions || device.value || 0) / device.clicks * 100) : 0),
        cpa: device.cpa || ((device.conversions || device.value || 0) > 0 ? (device.cost / (device.conversions || device.value || 1)) : 0),
        cos: device.cos || (device.conversionsValue > 0 ? (device.cost / device.conversionsValue * 100) : 0)
      }));
    }

    // Si on a le chart device, utiliser ses données
    if (!deviceChart) {
      return [];
    }

    let allDeviceData;

    // Appliquer le filtre par campagne si une campagne est sélectionnée
    if (selectedCampaignFilter && deviceChart.data) {
      // Vérifier si les données ont déjà été filtrées par le backend (pas de campaignId dans les entrées)
      const firstEntry = deviceChart.data[0];
      const hasNoCampaignId = firstEntry && !firstEntry.campaignId && !firstEntry.campaign_id;

      if (hasNoCampaignId) {
        // Les données sont déjà filtrées par le backend, utiliser directement
        console.log('📱 [DevicesTable] Données pré-filtrées par le backend, utilisation directe');
        allDeviceData = deviceChart.dataAggregated || deviceChart.data || [];
      } else {
        // Filtrage côté frontend (ancien comportement)
        const filteredDeviceData = {};

        deviceChart.data.forEach((deviceEntry) => {
          // Vérifier si cette entrée correspond à la campagne filtrée
          const matchesCampaign = deviceEntry.campaignId === selectedCampaignFilter.id ||
                                 deviceEntry.campaignId === selectedCampaignFilter.campaignId ||
                                 deviceEntry.campaignId?.toString() === selectedCampaignFilter.id?.toString() ||
                                 deviceEntry.campaignName === selectedCampaignFilter.name;

          if (matchesCampaign) {
            const deviceName = deviceEntry.name || deviceEntry.device || deviceEntry.deviceType || 'Appareil inconnu';

            if (!filteredDeviceData[deviceName]) {
              filteredDeviceData[deviceName] = {
                name: deviceName,
                impressions: 0,
                clicks: 0,
                cost: 0,
                conversions: 0,
                value: 0,
                conversionsValue: 0
              };
            }

            const device = filteredDeviceData[deviceName];
            device.impressions += deviceEntry.impressions || 0;
            device.clicks += deviceEntry.clicks || 0;
            device.cost += deviceEntry.cost || 0;
            device.conversions += deviceEntry.conversions || 0;
            device.value += deviceEntry.value || deviceEntry.conversions || 0;
            device.conversionsValue += deviceEntry.conversionsValue || 0;
          }
        });

        allDeviceData = Object.values(filteredDeviceData);
      }

    } else if (selectedCampaignTypeFilter && deviceChart.data) {
      // Vérifier si les données ont déjà été filtrées par le backend (pas de campaignId dans les entrées)
      const firstEntry = deviceChart.data[0];
      const hasNoCampaignId = firstEntry && !firstEntry.campaignId && !firstEntry.campaign_id;

      if (hasNoCampaignId) {
        // Les données sont déjà filtrées par le backend, utiliser directement
        console.log('📱 [DevicesTable] Données pré-filtrées par type de campagne, utilisation directe');
        allDeviceData = deviceChart.dataAggregated || deviceChart.data || [];
      } else {
        // Filtrage par type de campagne côté frontend
        const matchingCampaignIds = campaignsData
          .filter(campaign => {
            const campaignType = campaign.advertisingChannelType || campaign.type || campaign.campaignType;
            return campaignType === selectedCampaignTypeFilter.type ||
                   String(campaignType) === String(selectedCampaignTypeFilter.type) ||
                   campaignType === selectedCampaignTypeFilter.name ||
                   String(campaignType) === String(selectedCampaignTypeFilter.name);
          })
          .map(campaign => campaign.id || campaign.campaignId);

        const filteredDeviceData = {};

        deviceChart.data.forEach((deviceEntry) => {
          const deviceCampaignId = deviceEntry.campaignId || deviceEntry.campaign_id;
          const matchesCampaignType = matchingCampaignIds.includes(deviceCampaignId) ||
                                     matchingCampaignIds.includes(String(deviceCampaignId)) ||
                                     matchingCampaignIds.includes(Number(deviceCampaignId)) ||
                                     matchingCampaignIds.some(id => String(id) === String(deviceCampaignId));

          if (matchesCampaignType) {
            const deviceName = deviceEntry.device || deviceEntry.name || deviceEntry.deviceType || 'Appareil inconnu';

            if (!filteredDeviceData[deviceName]) {
              filteredDeviceData[deviceName] = {
                name: deviceName,
                impressions: 0,
                clicks: 0,
                cost: 0,
                conversions: 0,
                value: 0,
                conversionsValue: 0
              };
            }

            const device = filteredDeviceData[deviceName];
            device.impressions += deviceEntry.impressions || 0;
            device.clicks += deviceEntry.clicks || 0;
            device.cost += deviceEntry.cost || 0;
            device.conversions += deviceEntry.conversions || 0;
            device.value += deviceEntry.value || deviceEntry.conversions || 0;
            device.conversionsValue += deviceEntry.conversionsValue || 0;
          }
        });

        allDeviceData = Object.values(filteredDeviceData);
      }

    } else {
      // Pas de filtre, utiliser les données agrégées
      allDeviceData = deviceChart.dataAggregated || deviceChart.data || [];
    }

    // Appliquer le filtre par labels si des labels sont sélectionnés
    if (selectedLabelsFilter.length > 0 && deviceChart.data) {
      console.log('🏷️ [DevicesTable] Filtrage par labels:', selectedLabelsFilter);
      const firstEntry = deviceChart.data[0];
      const hasCampaignId = firstEntry && (firstEntry.campaignId || firstEntry.campaign_id);

      if (hasCampaignId) {
        // Filtrer les données individuelles et ré-agréger
        const filteredDeviceData = {};
        deviceChart.data.forEach((deviceEntry) => {
          const deviceCampaignId = deviceEntry.campaignId || deviceEntry.campaign_id;
          if (campaignMatchesLabelsFilter(deviceCampaignId)) {
            const deviceName = deviceEntry.device || deviceEntry.name || deviceEntry.deviceType || 'Appareil inconnu';
            if (!filteredDeviceData[deviceName]) {
              filteredDeviceData[deviceName] = { name: deviceName, impressions: 0, clicks: 0, cost: 0, conversions: 0, value: 0, conversionsValue: 0 };
            }
            const device = filteredDeviceData[deviceName];
            device.impressions += deviceEntry.impressions || 0;
            device.clicks += deviceEntry.clicks || 0;
            device.cost += deviceEntry.cost || 0;
            device.conversions += deviceEntry.conversions || 0;
            device.value += deviceEntry.value || deviceEntry.conversions || 0;
            device.conversionsValue += deviceEntry.conversionsValue || 0;
          }
        });
        allDeviceData = Object.values(filteredDeviceData);
        console.log('🏷️ [DevicesTable] Appareils après filtrage par labels:', allDeviceData.length);
      }
    }

    // Normaliser et calculer les métriques
    // NOTE: Ne pas arrondir les conversions ici pour éviter les erreurs d'agrégation
    // L'arrondi sera fait à l'affichage via formatNumber()
    return allDeviceData.map(device => ({
      ...device,
      name: device.name || device.device || device.deviceType || 'Appareil inconnu',
      ctr: device.ctr || (device.impressions > 0 ? (device.clicks / device.impressions * 100) : 0),
      cpc: device.cpc || (device.clicks > 0 ? (device.cost / device.clicks) : 0),
      conversions: device.conversions || device.value || 0,
      conversionRate: device.conversionRate || (device.clicks > 0 ? ((device.conversions || device.value || 0) / device.clicks * 100) : 0),
      cpa: device.cpa || ((device.conversions || device.value || 0) > 0 ? (device.cost / (device.conversions || device.value || 1)) : 0),
      cos: device.cos || (device.conversionsValue > 0 ? (device.cost / device.conversionsValue * 100) : 0)
    }));
  }, [deviceChart, selectedCampaignFilter, selectedCampaignTypeFilter, charts, campaignsData, selectedLabelsFilter, campaignMatchesLabelsFilter, labelFilteredData]);


  // Fonction de tri memoized
  const sortedDevices = useMemo(() => {
    if (!deviceData.length) return [];

    const sorted = [...deviceData];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Cas spécial pour le nom
      if (sortConfig.key === 'name') {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // Pour les valeurs numériques
      if (typeof aValue === 'string' && !isNaN(aValue)) aValue = parseFloat(aValue);
      if (typeof bValue === 'string' && !isNaN(bValue)) bValue = parseFloat(bValue);
      
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
      
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [deviceData, sortConfig]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!deviceData.length) return {};

    const baseTotals = deviceData.reduce((acc, device) => ({
      impressions: acc.impressions + (device.impressions || 0),
      clicks: acc.clicks + (device.clicks || 0),
      cost: acc.cost + (device.cost || 0),
      conversions: acc.conversions + (device.conversions || 0),
      conversionsValue: acc.conversionsValue + (device.conversionsValue || 0),
      ...cumulerVideo(acc, device)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [deviceData]);

  // Calcul des totaux de la période précédente
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousDevicesData.length) return null;

    const baseTotals = previousDevicesData.reduce((acc, device) => ({
      impressions: acc.impressions + (device.impressions || 0),
      clicks: acc.clicks + (device.clicks || 0),
      cost: acc.cost + (device.cost || 0),
      conversions: acc.conversions + (device.conversions || 0),
      conversionsValue: acc.conversionsValue + (device.conversionsValue || 0),
      ...cumulerVideo(acc, device)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousDevicesData]);

  // Données pour les graphiques en secteurs (clics et conversions)
  const clicksChartData = useMemo(() => {
    if (!deviceData.length) return [];
    
    return deviceData
      .map(device => ({
        name: device.name,
        value: device.clicks || 0,
        percentage: totals.clicks > 0 ? ((device.clicks / totals.clicks) * 100).toFixed(1) : 0
      }))
      .filter(device => parseFloat(device.percentage) > 0.0); // Filtrer les appareils avec 0,0% ou moins
  }, [deviceData, totals.clicks]);

  const conversionsChartData = useMemo(() => {
    if (!deviceData.length) return [];
    
    return deviceData
      .map(device => ({
        name: device.name,
        value: device.conversions || 0,
        percentage: totals.conversions > 0 ? ((device.conversions / totals.conversions) * 100).toFixed(1) : 0
      }))
      .filter(device => parseFloat(device.percentage) > 0.0); // Filtrer les appareils avec 0,0% ou moins
  }, [deviceData, totals.conversions]);

  // Données communes pour la légende (tous les appareils avec leurs couleurs)
  const legendData = useMemo(() => {
    if (!deviceData.length) return [];
    
    return deviceData.map((device, index) => ({
      value: device.name,
      type: 'rect',
      color: COLORS[index % COLORS.length]
    }));
  }, [deviceData, COLORS]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Ne pas rendre si pas de données
  if (!deviceData.length) {
    return null;
  }

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  return (
    <div className="device-conversions-section">
      <div className="section-header">
        <h4><Smartphone size={16} />&nbsp;Appareils</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Bouton configuration des métriques - visible uniquement pour non-clients */}
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Appareils"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Indicateur de chargement des données de comparaison */}
          {compareEnabled && (loadingComparison || loadingPrevious) && (
            <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des données de comparaison...
            </div>
          )}
          {/* Graphiques en secteurs côte à côte */}
          {(!compareEnabled || (!loadingComparison && !loadingPrevious)) && (
          <>
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {/* Pie chart Clics (gauche) */}
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Clics par appareil</h5>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={clicksChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percentage }) => `${percentage}%`}
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {clicksChartData.map((_, index) => (
                          <Cell key={`clicks-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [value + ' clics', name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Légende commune (centre) */}
                <div style={{ 
                  minWidth: '120px', 
                  padding: '0 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  {legendData.map((item, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '8px',
                      fontSize: '13px'
                    }}>
                      <div style={{ 
                        width: '12px', 
                        height: '12px', 
                        backgroundColor: item.color, 
                        marginRight: '8px',
                        borderRadius: '2px'
                      }}></div>
                      <span>{item.value}</span>
                    </div>
                  ))}
                </div>
                
                {/* Pie chart Conversions (droite) */}
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Conversions par appareil</h5>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={conversionsChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percentage }) => `${percentage}%`}
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {conversionsChartData.map((_, index) => (
                          <Cell key={`conversions-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [value + ' conversions', name]} />
                    </PieChart>
                  </ResponsiveContainer>
            </div>
            </div>
          </div>

          {/* Tableau détaillé */}
          <div className="device-table-wrapper" style={{ marginTop: '0', paddingTop: '0' }}>
            <div className="device-summary-table" style={{ marginTop: '0', paddingTop: '0' }}>
              <table 
                key={selectedMetrics.filter(m => m.selected).map(m => m.key).join(',')}
                className="devices-table"
                style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}
              >
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="sortable">
                      Appareil {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    {/* Rendu dynamique des colonnes selon l'ordre configuré */}
                    {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                      const config = metricConfig[metricKey];
                      return (
                        <th key={metricKey} onClick={() => handleSort(config.sortKey)} className="sortable numeric">
                          {config.label} {sortConfig.key === config.sortKey && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {sortedDevices.map((device, index) => {
                    // Récupérer les données de comparaison pour cet appareil
                    const prevDevice = previousDevicesMap[device.name] || {};
                    const hasComparison = compareEnabled && Object.keys(prevDevice).length > 0;

                    return (
                      <tr key={index}>
                        <td className="device-name">
                          <span
                            className="device-indicator"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></span>
                          {device.name}
                        </td>
                        {/* Rendu dynamique des cellules selon l'ordre configuré */}
                        {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                          const config = metricConfig[metricKey];
                          const className = config.getClassName ? `numeric ${config.getClassName(device)}` : 'numeric';
                          return (
                            <td key={metricKey} className={className} style={{ verticalAlign: 'top' }}>
                              {config.renderCell(device, prevDevice, hasComparison)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot>
                  <tr className="total-row">
                    <td className="device-name"><strong>TOTAL</strong></td>
                    {/* Rendu dynamique des totaux selon l'ordre configuré */}
                    {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                      const config = metricConfig[metricKey];
                      const className = config.getTotalClassName ? `numeric ${config.getTotalClassName(totals)}` : 'numeric';
                      return (
                        <td key={metricKey} className={className} style={{ verticalAlign: 'top' }}>
                          {config.renderTotal(totals, previousTotals)}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          </>
          )}
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="devices"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Appareils"
      />
    </div>
  );
});

DevicesTable.displayName = 'DevicesTable';

export default DevicesTable;